import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, LogOut, Trash2, Phone, Mail, CalendarDays, ArrowLeft, CheckCircle2, XCircle, PhoneCall, Kanban } from 'lucide-react';
import { Wordmark } from './Logo';

const API = process.env.REACT_APP_BACKEND_URL;
const ESTADOS = ['pendiente', 'confirmada', 'completada', 'cancelada'];

const ESTADO_STYLE = {
    pendiente: 'bg-pastelcream text-ink border-ink/20',
    confirmada: 'bg-green-100 text-green-800 border-green-300',
    completada: 'bg-pastelblue text-ink border-ink/20',
    cancelada: 'bg-red-100 text-crimson border-crimson/30',
};

function formatApiErrorDetail(detail) {
    if (detail == null) return 'Algo salio mal. Intentalo de nuevo.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
}

export async function apiFetch(url, opts = {}) {
    let res = await fetch(url, { ...opts, credentials: 'include' });
    if (res.status === 401) {
        const r2 = await fetch(`${API}/api/auth/refresh`, { method: 'POST', credentials: 'include' });
        if (r2.ok) res = await fetch(url, { ...opts, credentials: 'include' });
    }
    return res;
}

const fmtFecha = (d) => {
    if (!d) return 'Por confirmar';
    try {
        return new Date(`${d}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {
        return d;
    }
};

export function Login({ onLogin, title = 'Panel de citas' }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        setError('');
        try {
            const res = await fetch(`${API}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json().catch(() => null);
            if (!res.ok) throw new Error(formatApiErrorDetail(data?.detail));
            onLogin(data);
        } catch (err) {
            setError(err.message || 'No se pudo iniciar sesion.');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="min-h-screen bg-ink flex items-center justify-center px-4" data-testid="admin-login-view">
            <div className="w-full max-w-md bg-white rounded-[1.6rem] p-8 lg:p-10 shadow-2xl">
                <div className="flex justify-center mb-8"><Wordmark compact /></div>
                <h1 className="font-display text-2xl font-semibold text-ink text-center">{title}</h1>
                <p className="text-center text-ink/60 text-sm mt-2 mb-8">Acceso privado del equipo</p>
                <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/60">Email</label>
                        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full mt-2 rounded-xl border border-ink/15 px-4 py-3 text-[15px] focus:border-crimson focus:outline-none" data-testid="admin-login-email" />
                    </div>
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/60">Contrasena</label>
                        <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full mt-2 rounded-xl border border-ink/15 px-4 py-3 text-[15px] focus:border-crimson focus:outline-none" data-testid="admin-login-password" />
                    </div>
                    {error && <p className="text-sm text-crimson font-semibold" data-testid="admin-login-error">{error}</p>}
                    <button type="submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-crimson px-6 py-3.5 text-[13px] font-bold uppercase tracking-[0.14em] text-pastelcream hover:bg-crimsondark transition-colors disabled:opacity-60" data-testid="admin-login-submit">
                        {sending ? <Loader2 size={16} className="animate-spin" /> : null}
                        {sending ? 'Entrando...' : 'Entrar'}
                    </button>
                </form>

                <div className="my-6 flex items-center gap-3 text-ink/35 text-[10px] font-bold uppercase tracking-[0.24em]">
                    <span className="h-px flex-1 bg-ink/10" />
                    o
                    <span className="h-px flex-1 bg-ink/10" />
                </div>

                {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                <button
                    onClick={() => {
                        const redirectUrl = window.location.origin + '/admin';
                        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    }}
                    className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-ink/15 bg-white px-6 py-3.5 text-[13px] font-bold tracking-[0.06em] text-ink hover:border-ink/40 hover:shadow-md transition-all"
                    data-testid="admin-google-button"
                >
                    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                    </svg>
                    Continuar con Google
                </button>
                <a href="/" className="block mt-8 text-center text-sm text-ink/50 hover:text-crimson transition-colors" data-testid="admin-back-to-site">Volver a la web</a>
            </div>
        </div>
    );
}

function CitaCard({ a, index, onStatus, onDelete }) {
    const [deleting, setDeleting] = useState(false);

    const remove = async () => {
        if (!window.confirm(`Eliminar la cita de ${a.nombre}?`)) return;
        setDeleting(true);
        const res = await apiFetch(`${API}/api/appointments/${a._id}`, { method: 'DELETE' });
        setDeleting(false);
        if (res.ok) { toast.success('Cita eliminada'); onDelete(a._id); }
        else toast.error('No se pudo eliminar');
    };

    const changeStatus = async (status) => {
        const res = await apiFetch(`${API}/api/appointments/${a._id}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
        });
        if (res.ok) { toast.success(`Cita marcada como ${status}`); onStatus(a._id, status); }
        else toast.error('No se pudo actualizar');
    };

    return (
        <article className="bg-white rounded-2xl border border-ink/10 p-6 flex flex-col gap-4" data-testid={`admin-card-${index}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="font-display text-xl font-semibold text-ink">{a.nombre}</h3>
                    <p className="text-sm text-ink/60">{a.empresa}</p>
                </div>
                <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-[0.14em] rounded-full px-3 py-1 border ${ESTADO_STYLE[a.status] || ESTADO_STYLE.pendiente}`} data-testid={`admin-status-chip-${index}`}>
                        {a.status || 'pendiente'}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] rounded-full px-3 py-1 border border-crimson/25 text-crimson">{a.servicio}</span>
                </div>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink/75">
                <span className="inline-flex items-center gap-2" data-testid={`admin-card-email-${index}`}><Mail size={14} className="text-crimson" /><a className="hover:text-crimson" href={`mailto:${a.email}`}>{a.email}</a></span>
                <span className="inline-flex items-center gap-2" data-testid={`admin-card-phone-${index}`}><Phone size={14} className="text-crimson" /><a className="hover:text-crimson" href={`tel:${a.telefono}`}>{a.telefono}</a></span>
                <span className="inline-flex items-center gap-2" data-testid={`admin-card-fecha-${index}`}><CalendarDays size={14} className="text-crimson" />{fmtFecha(a.fecha_preferida)}</span>
            </div>

            {a.mensaje && <p className="text-sm text-ink/70 leading-relaxed bg-canvas rounded-xl p-3 border border-ink/5">{a.mensaje}</p>}

            <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                <select
                    value={a.status || 'pendiente'}
                    onChange={(e) => changeStatus(e.target.value)}
                    className="rounded-full border border-ink/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] text-ink bg-white"
                    data-testid={`admin-status-select-${index}`}
                >
                    {ESTADOS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={remove} disabled={deleting} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-ink/40 hover:text-crimson transition-colors disabled:opacity-50" data-testid={`admin-delete-${index}`}>
                    {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    Eliminar
                </button>
            </div>
        </article>
    );
}

function CallbacksSection() {
    const [cbs, setCbs] = useState(null);

    const load = async () => {
        const res = await apiFetch(`${API}/api/callbacks`);
        if (res.ok) setCbs(await res.json());
    };

    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm('Eliminar esta solicitud de llamada?')) return;
        const res = await apiFetch(`${API}/api/callbacks/${id}`, { method: 'DELETE' });
        if (res.ok) { toast.success('Solicitud eliminada'); setCbs((cs) => (cs || []).filter((c) => c._id !== id)); }
        else toast.error('No se pudo eliminar');
    };

    return (
        <div className="bg-white rounded-2xl border border-crimson/20 p-6" data-testid="admin-callbacks">
            <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2.5">
                <PhoneCall size={19} className="text-crimson" />
                Solicitudes de llamada (Te llamamos)
                <span className="font-ink text-[11px] text-ink/40 font-normal">
                    {cbs ? `${cbs.length} pendiente${cbs.length === 1 ? '' : 's'}` : ''}
                </span>
            </h2>
            {cbs === null && (
                <p className="mt-4 text-sm text-ink/50 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Cargando...</p>
            )}
            {cbs !== null && cbs.length === 0 && (
                <p className="mt-4 text-sm text-ink/50" data-testid="admin-callbacks-empty">No hay solicitudes de llamada pendientes.</p>
            )}
            {cbs !== null && cbs.length > 0 && (
                <ul className="mt-5 space-y-3">
                    {cbs.map((c, i) => (
                        <li key={c._id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-canvas border border-ink/10 px-4 py-3" data-testid={`admin-callback-card-${i + 1}`}>
                            <div className="flex items-center gap-4 text-sm">
                                <span className="font-bold text-ink">{c.nombre}</span>
                                <a href={`tel:${c.telefono}`} className="inline-flex items-center gap-1.5 text-crimson font-semibold hover:underline" data-testid={`admin-callback-phone-${i + 1}`}>
                                    <Phone size={13} /> {c.telefono}
                                </a>
                                <span className="font-ink text-[10px] text-ink/40">{(c.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                            </div>
                            <button
                                onClick={() => remove(c._id)}
                                className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-ink/40 hover:text-crimson transition-colors"
                                data-testid={`admin-callback-delete-${i + 1}`}
                            >
                                <Trash2 size={13} /> Eliminar
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function Dashboard({ user, onLogout }) {
    const [citas, setCitas] = useState(null);
    const [filter, setFilter] = useState('todas');
    const today = new Date().toISOString().split('T')[0];

    const load = async () => {
        const res = await apiFetch(`${API}/api/appointments`);
        if (res.ok) setCitas(await res.json());
        else if (res.status === 401) onLogout();
    };

    useEffect(() => { load(); }, []);

    const onStatus = (id, status) => setCitas((cs) => cs.map((c) => (c._id === id ? { ...c, status } : c)));
    const onDelete = (id) => setCitas((cs) => cs.filter((c) => c._id !== id));

    const count = (s) => (citas || []).filter((c) => (c.status || 'pendiente') === s).length;
    const hoy = (citas || []).filter((c) => c.fecha_preferida === today).length;
    const visible = (citas || []).filter((c) => {
        if (filter === 'todas') return true;
        if (filter === 'hoy') return c.fecha_preferida === today;
        return (c.status || 'pendiente') === filter;
    });

    const FILTERS = ['todas', 'hoy', 'pendiente', 'confirmada', 'completada', 'cancelada'];

    return (
        <div className="min-h-screen bg-canvas" data-testid="admin-dashboard">
            <header className="bg-ink text-pastelcream">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Wordmark dark compact />
                        <span className="hidden sm:inline text-[11px] font-ink uppercase tracking-[0.2em] text-pastelcream/50 border-l border-pastelcream/20 pl-4">
                            Panel de citas
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <a href="/crm" className="text-[11px] font-bold uppercase tracking-[0.14em] text-pastelcream/60 hover:text-pastelcream inline-flex items-center gap-1.5" data-testid="admin-link-crm">
                            <Kanban size={13} /> CRM
                        </a>
                        <a href="/" className="text-[11px] font-bold uppercase tracking-[0.14em] text-pastelcream/60 hover:text-pastelcream inline-flex items-center gap-1.5" data-testid="admin-link-site">
                            <ArrowLeft size={13} /> Web
                        </a>
                        <button onClick={onLogout} className="inline-flex items-center gap-2 rounded-full border border-pastelcream/25 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] hover:border-crimson hover:text-crimson transition-colors" data-testid="admin-logout">
                            <LogOut size={13} /> Salir
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
                <CallbacksSection />

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="admin-stats">
                    {[
                        ['Total', citas ? citas.length : '-', 'admin-stat-total'],
                        ['Para hoy', citas ? hoy : '-', 'admin-stat-hoy'],
                        ['Pendientes', citas ? count('pendiente') : '-', 'admin-stat-pendiente'],
                        ['Confirmadas', citas ? count('confirmada') : '-', 'admin-stat-confirmada'],
                    ].map(([label, val, tid]) => (
                        <div key={label} className="bg-white rounded-2xl border border-ink/10 p-5" data-testid={tid}>
                            <div className="font-display text-3xl font-semibold text-ink">{val}</div>
                            <div className="text-[11px] uppercase tracking-[0.16em] text-ink/50 font-semibold mt-1">{label}</div>
                        </div>
                    ))}
                </div>

                <div className="flex flex-wrap gap-2 mt-8" data-testid="admin-filters">
                    {FILTERS.map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] border transition-all ${filter === f ? 'bg-ink text-pastelcream border-ink' : 'bg-white text-ink/60 border-ink/15 hover:border-crimson hover:text-crimson'}`}
                            data-testid={`admin-filter-${f}`}
                        >
                            {f}
                        </button>
                    ))}
                </div>

                <div className="mt-6 space-y-4 pb-16">
                    {citas === null && (
                        <div className="flex items-center gap-3 text-ink/60 p-8" data-testid="admin-loading">
                            <Loader2 size={18} className="animate-spin" /> Cargando citas...
                        </div>
                    )}
                    {citas !== null && visible.length === 0 && (
                        <div className="bg-white rounded-2xl border border-dashed border-ink/20 p-10 text-center text-ink/50" data-testid="admin-empty">
                            <CheckCircle2 size={22} className="mx-auto mb-3 text-crimson" />
                            No hay citas en esta vista.
                        </div>
                    )}
                    {visible.map((a, i) => (
                        <CitaCard key={a._id} a={a} index={i} onStatus={onStatus} onDelete={onDelete} />
                    ))}
                </div>
            </main>
        </div>
    );
}

export default function AdminPanel() {
    const [user, setUser] = useState(null);

    useEffect(() => {
        fetch(`${API}/api/auth/me`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error('no session'))))
            .then(setUser)
            .catch(() => setUser(false));
    }, []);

    if (user === null) {
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center text-pastelcream" data-testid="admin-checking">
                <Loader2 size={22} className="animate-spin" />
            </div>
        );
    }

    if (user && user.role !== 'admin') {
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center px-4" data-testid="admin-denied">
                <div className="bg-white rounded-[1.6rem] p-10 max-w-md text-center">
                    <XCircle size={34} className="mx-auto text-crimson" />
                    <h2 className="font-display text-2xl font-semibold text-ink mt-5">Acceso restringido</h2>
                    <p className="text-ink/65 text-sm mt-3 leading-relaxed">
                        La cuenta <strong>{user.email}</strong> no tiene permisos para el panel de citas.
                    </p>
                    <button
                        onClick={async () => { await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' }); setUser(false); }}
                        className="mt-8 rounded-full bg-ink px-6 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-pastelcream hover:bg-crimson transition-colors"
                        data-testid="admin-denied-logout"
                    >
                        Cerrar sesion
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {user ? <Dashboard user={user} onLogout={() => setUser(false)} /> : <Login onLogin={setUser} />}
        </>
    );
}
